import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Ship, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ADMIN_PASSWORD_MIN_LENGTH, getAdminPasswordIssue } from '@harbormaster/shared';
import { useAuth } from '../hooks/useAuth';
import { tAdminPasswordIssue } from '../lib/passwordValidation';

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const { updatePassword } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    const pwdIssue = getAdminPasswordIssue(password);
    if (pwdIssue) {
      setError(tAdminPasswordIssue(t, pwdIssue));
      return;
    }
    if (password !== confirm) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }
    setError('');
    setSubmitting(true);
    const { error: err } = await updatePassword(password);
    setSubmitting(false);
    if (err) {
      setError(err);
    } else {
      setDone(true);
      setTimeout(() => navigate('/'), 2000);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <Ship className="w-8 h-8 text-brand-600" />
          <h1 className="text-xl font-bold text-gray-900">Harbormaster</h1>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {done ? (
            <div className="text-center py-4">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('auth.passwordUpdated')}</h2>
            </div>
          ) : (
            <>
              <h2 className="text-lg font-semibold text-gray-900 mb-1">{t('auth.resetPassword')}</h2>
              <p className="text-sm text-gray-500 mb-6">{t('auth.resetPasswordSubtitle')}</p>

              {error && (
                <div className="mb-4 px-3 py-2.5 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.newPassword')}</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={ADMIN_PASSWORD_MIN_LENGTH}
                    autoFocus
                    autoComplete="new-password"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40 focus:border-brand-500"
                  />
                  <p className="text-xs text-gray-500 mt-1.5">{t('auth.passwordRequirementsHint')}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.confirmPassword')}</label>
                  <input
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    minLength={ADMIN_PASSWORD_MIN_LENGTH}
                    autoComplete="new-password"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40 focus:border-brand-500"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting || !password || !confirm}
                  className="w-full py-2.5 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {submitting ? t('auth.settingPassword') : t('auth.setPassword')}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
